import { html, type PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared, authStyles } from "../styles.js";
import { parseError, errorMessage, humanError } from "../api/errors.js";
import {
  requestOtp,
  authWithOtp,
  completeTotpLogin,
  loginWithPasskey,
  passkeySupported,
} from "../api/account.js";
import { navigate } from "../nav.js";

/**
 * page-login is passwordless: enter an email, receive a one-time code by mail,
 * enter it to sign in (PocketBase's native request-otp → auth-with-otp). Accounts
 * with two-factor enabled answer 401 {mfaId}; the page then asks for the
 * authenticator code and completes via /api/registry/auth/totp/verify. A passkey
 * button offers usernameless WebAuthn login. After login it returns to the `next`
 * route or `/`; the router then routes handle-less accounts to onboarding.
 * (Administrators use the PocketBase UI at /_/.)
 */
@customElement("page-login")
export class PageLogin extends PageElement {
  static override styles = [shared, authStyles];

  @state() private email = "";
  @state() private errorMsg = "";
  @state() private submitting = false;
  @state() private step: "email" | "code" | "totp" = "email";
  @state() private otpId = "";
  @state() private mfaId = "";
  @state() private code = "";

  override firstUpdated() {
    this.setTitle("Sign in");
    super.firstUpdated();
  }

  override updated(changed: PropertyValues) {
    if (changed.has("step")) {
      const id = this.step === "code" ? "#otp" : this.step === "totp" ? "#totp" : null;
      if (id) (this.renderRoot.querySelector(id) as HTMLInputElement | null)?.focus();
    }
  }

  private redirectNext() {
    const next = new URLSearchParams(location.search).get("next") || "/";
    navigate(next, { replace: true });
  }

  private async submitEmail(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.submitting = true;
    try {
      this.otpId = await requestOtp(this.email.trim());
      this.code = "";
      this.step = "code";
    } catch (err) {
      this.errorMsg =
        parseError(err).code === "rate_limited" ? errorMessage("rate_limited") : humanError(err);
    } finally {
      this.submitting = false;
    }
  }

  private async submitCode(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.submitting = true;
    try {
      await authWithOtp(this.otpId, this.code.trim());
      this.redirectNext();
    } catch (err) {
      const { code, mfaId } = parseError(err);
      if (code === "mfa_required" && mfaId) {
        this.mfaId = mfaId;
        this.code = "";
        this.step = "totp";
      } else if (code === "rate_limited") {
        this.errorMsg = errorMessage("rate_limited");
      } else {
        this.errorMsg = "That code is not valid or has expired. Request a new one.";
      }
    } finally {
      this.submitting = false;
    }
  }

  private async submitTotp(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.submitting = true;
    try {
      await completeTotpLogin(this.mfaId, this.code.trim());
      this.redirectNext();
    } catch (err) {
      const { code } = parseError(err);
      if (code === "invalid_totp") {
        this.errorMsg = errorMessage("invalid_totp");
      } else {
        this.errorMsg = "The sign-in attempt expired. Please start again.";
        this.step = "email";
        this.mfaId = "";
      }
    } finally {
      this.submitting = false;
    }
  }

  private async resend() {
    if (this.submitting) return;
    this.errorMsg = "";
    this.submitting = true;
    try {
      this.otpId = await requestOtp(this.email.trim());
      this.code = "";
    } catch (err) {
      this.errorMsg =
        parseError(err).code === "rate_limited" ? errorMessage("rate_limited") : humanError(err);
    } finally {
      this.submitting = false;
    }
  }

  private async passkeyLogin() {
    this.errorMsg = "";
    this.submitting = true;
    try {
      await loginWithPasskey();
      this.redirectNext();
    } catch (err) {
      // A user-cancelled ceremony throws too; keep the message soft.
      this.errorMsg = "Passkey sign-in was cancelled or failed. Try again, or use your email.";
      console.debug("passkey login failed", err);
    } finally {
      this.submitting = false;
    }
  }

  override render() {
    return html`
      <div class="auth-card">
        <div class="mark">
          <span class="glyph" aria-hidden="true">b</span>
          <span class="word">bino Registry</span>
        </div>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.step === "email"
          ? this.renderEmail()
          : this.step === "code"
            ? this.renderCode()
            : this.renderTotp()}
      </div>
    `;
  }

  private renderEmail() {
    return html`
      <form @submit=${this.submitEmail}>
        <h1>Sign in</h1>
        <p class="lede">Enter your email and we'll send you a one-time code. No password needed.</p>
        <div class="field">
          <label for="email">Email</label>
          <input
            id="email"
            type="email"
            autocomplete="username webauthn"
            placeholder="you@company.com"
            .value=${this.email}
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <div class="actions">
          <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
            ${this.submitting ? "Sending code…" : "Send code"}
          </button>
        </div>
        ${passkeySupported()
          ? html`
              <div class="sso-sep">or</div>
              <div class="actions">
                <button
                  class="btn"
                  type="button"
                  ?disabled=${this.submitting}
                  @click=${this.passkeyLogin}
                >
                  Sign in with a passkey
                </button>
              </div>
            `
          : ""}
        <p class="alt">No account? <a href=${"/signup" + location.search}>Create one</a></p>
      </form>
    `;
  }

  private renderCode() {
    return html`
      <form @submit=${this.submitCode}>
        <h1>Check your email</h1>
        <p class="lede">We sent a sign-in code to <b>${this.email}</b>. Enter it below to sign in.</p>
        <div class="field">
          <label for="otp">Code</label>
          <input
            id="otp"
            class="code-input"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="123456"
            .value=${this.code}
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <div class="actions">
          <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
            ${this.submitting ? "Verifying…" : "Sign in"}
          </button>
        </div>
        <div class="alt">
          <button type="button" ?disabled=${this.submitting} @click=${this.resend}>
            Resend code</button
          >&nbsp;·&nbsp;<button
            type="button"
            @click=${() => {
              this.step = "email";
              this.otpId = "";
              this.errorMsg = "";
            }}
          >
            Use a different email
          </button>
        </div>
      </form>
    `;
  }

  private renderTotp() {
    return html`
      <form @submit=${this.submitTotp}>
        <h1>Two-factor code</h1>
        <p class="lede">Enter the 6-digit code from your authenticator app.</p>
        <div class="field">
          <label for="totp">Code</label>
          <input
            id="totp"
            class="code-input"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]{6}"
            maxlength="6"
            .value=${this.code}
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <div class="actions">
          <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
            ${this.submitting ? "Verifying…" : "Verify"}
          </button>
        </div>
        <div class="alt">
          <button
            type="button"
            @click=${() => {
              this.step = "email";
              this.mfaId = "";
              this.errorMsg = "";
            }}
          >
            Start over
          </button>
        </div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-login": PageLogin;
  }
}
